import { useSelector } from 'react-redux';
import { userIdSelector } from 'domain/env';
import { chatAllUsersSelector } from 'domain/chat';
import { extractEmail } from 'lib/helpers';

// Minimal shape of a chat participant consumed by the favorites sidebar. The chat domain is legacy
// JS — chatAllUsersSelector returns an untyped Immutable List — so we cast to this local type at the
// selector boundary rather than typing the whole chat domain here (out of ticket scope).
type ChatUser = { userId: string; username?: string; picture?: string };

const parseSearchLink = (searchLink: string): string[][] =>
  decodeURIComponent(searchLink)
    .split('&')
    .map((item) => item.split('='));

const useFavorites = () => {
  const userId = useSelector(userIdSelector);
  // Single documented cast at the legacy-chat boundary (see ChatUser above).
  const users = useSelector(chatAllUsersSelector) as unknown as ChatUser[];

  const getUser = (userID: string): ChatUser | undefined => users.find((u) => u.userId === userID);

  const getUserBySearchLink = (searchLink: string | null | undefined): ChatUser | null => {
    if (!searchLink) return null;
    const tags = parseSearchLink(searchLink);
    // A malformed favorite (e.g. saved with an empty global query → "?") yields a segment with no
    // "=", so tags[0][1] is undefined; guard before extractEmail, which does `str.match` and throws.
    const value = tags.length === 1 ? tags[0][1] : undefined;
    const userID = value ? extractEmail(value) : null;
    return userID ? getUser(userID) ?? null : null;
  };

  const isCurrentUser = (userFilter: string) => userFilter === userId;

  return { getUserBySearchLink, isCurrentUser };
};

export default useFavorites;
